<template>
  <div class="nfse-settings-modern">
    <NavBar />
    <AuthBySessionExpired />

    <div class="nfse-settings-container">
      <!-- Header Section -->
      <div class="nfse-settings-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="handleToBack">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-file-invoice title-icon"></i>
              Nota Fiscal de Serviço
            </h1>
            <p class="page-subtitle">
              Configure a emissão de NFS-e para esta instituição
            </p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="permission('write', 'nfse')"
              class="btn-add-modern"
              :loading="saving"
              :disabled="!canSave"
              @click="save"
            >
              <i class="fas fa-save mr-2"></i>
              Salvar
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="nfse-settings-content">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <ErrorsMessages :errors="errors" />

          <!-- Status do módulo -->
          <div class="settings-card">
            <div class="card-header">
              <i class="fas fa-toggle-on card-icon"></i>
              <div>
                <h3 class="card-title">Status do módulo</h3>
                <p class="card-subtitle">
                  Liga ou desliga a emissão de NFS-e para esta instituição
                </p>
              </div>
            </div>

            <div class="card-body">
              <div class="field-row">
                <div class="field-group switch-group">
                  <v-switch
                    v-model="form.active"
                    inset
                    hide-details
                    color="#2E7D5B"
                    :disabled="!permission('write', 'nfse')"
                  />
                  <span class="switch-label">
                    Módulo {{ form.active ? "ativo" : "inativo" }}
                  </span>
                </div>

                <div class="field-group">
                  <label class="field-label">Ambiente</label>
                  <v-select
                    v-model="form.environment"
                    :items="environmentOptions"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div
                v-if="
                  form.environment === 'production' &&
                  !form.homologation_confirmed
                "
                class="alert-box"
              >
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Confirme um teste em homologação (mais abaixo) antes de ativar
                em produção.
              </div>
            </div>
          </div>

          <!-- Dados fiscais -->
          <div class="settings-card">
            <div class="card-header">
              <i class="fas fa-landmark card-icon"></i>
              <div>
                <h3 class="card-title">Dados fiscais</h3>
                <p class="card-subtitle">
                  Usados na emissão da nota junto à prefeitura
                </p>
              </div>
            </div>

            <div class="card-body">
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">CNPJ</label>
                  <v-text-field
                    v-model="form.company_cnpj"
                    placeholder="00.000.000/0000-00"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">Razão social</label>
                  <v-text-field
                    v-model="form.company_legal_name"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group field-group-wide">
                  <label class="field-label">Logradouro</label>
                  <v-text-field
                    v-model="form.address_street"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">Número</label>
                  <v-text-field
                    v-model="form.address_number"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Complemento</label>
                  <v-text-field
                    v-model="form.address_complement"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">Bairro</label>
                  <v-text-field
                    v-model="form.address_district"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">CEP</label>
                  <v-text-field
                    v-model="form.address_zipcode"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">UF</label>
                  <v-text-field
                    v-model="form.address_state"
                    maxlength="2"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Padrão de NFS-e</label>
                  <v-select
                    v-model="form.nfse_model"
                    :items="nfseModelOptions"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">Código IBGE do município</label>
                  <v-text-field
                    v-model="form.city_ibge_code"
                    placeholder="Ex: 3550308"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Regime tributário</label>
                  <v-select
                    v-model="form.tax_regime"
                    :items="taxRegimeOptions"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">Inscrição municipal</label>
                  <v-text-field
                    v-model="form.municipal_registration"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group checkbox-group">
                  <v-checkbox
                    v-model="form.simples_nacional_optant"
                    label="Optante pelo Simples Nacional"
                    hide-details
                    color="#1B4965"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Serviço padrão -->
          <div class="settings-card">
            <div class="card-header">
              <i class="fas fa-concierge-bell card-icon"></i>
              <div>
                <h3 class="card-title">Serviço padrão</h3>
                <p class="card-subtitle">
                  Usado quando o lançamento não define um código específico
                </p>
              </div>
            </div>

            <div class="card-body">
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">Código do serviço</label>
                  <v-text-field
                    v-model="form.default_service_code"
                    placeholder="Ex: 06.02"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group field-group-wide">
                  <label class="field-label">Descrição do serviço</label>
                  <v-text-field
                    v-model="form.default_service_description"
                    placeholder="Ex: Assistência a idosos"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Credenciais Focus NFe -->
          <div class="settings-card">
            <div class="card-header">
              <i class="fas fa-key card-icon"></i>
              <div>
                <h3 class="card-title">Credenciais Focus NFe</h3>
                <p class="card-subtitle">
                  Tokens gerados no painel da Focus, um por ambiente
                </p>
              </div>
            </div>

            <div class="card-body">
              <div class="field-row">
                <div class="field-group">
                  <label class="field-label">
                    Token — Sandbox
                    <span
                      class="status-badge"
                      :class="
                        config.has_focus_token_sandbox
                          ? 'occupied'
                          : 'available'
                      "
                    >
                      {{
                        config.has_focus_token_sandbox
                          ? "Configurado"
                          : "Não configurado"
                      }}
                    </span>
                  </label>
                  <v-text-field
                    v-model="form.focus_token_sandbox"
                    type="password"
                    placeholder="Deixe em branco para manter o token atual"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>

                <div class="field-group">
                  <label class="field-label">
                    Token — Produção
                    <span
                      class="status-badge"
                      :class="
                        config.has_focus_token_production
                          ? 'occupied'
                          : 'available'
                      "
                    >
                      {{
                        config.has_focus_token_production
                          ? "Configurado"
                          : "Não configurado"
                      }}
                    </span>
                  </label>
                  <v-text-field
                    v-model="form.focus_token_production"
                    type="password"
                    placeholder="Deixe em branco para manter o token atual"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div class="field-row">
                <div class="field-group checkbox-group">
                  <v-checkbox
                    v-model="form.homologation_confirmed"
                    label="Confirmo que testei a emissão em homologação (sandbox) com sucesso"
                    hide-details
                    color="#2E7D5B"
                    :disabled="!permission('write', 'nfse')"
                  />
                </div>
              </div>

              <div
                v-if="config.certificate_status !== 'not_sent'"
                class="cert-status-row"
              >
                <i class="fas fa-certificate mr-2"></i>
                Certificado digital: <b>{{ certificateStatusLabel }}</b>
                <span v-if="config.certificate_expires_at">
                  — validade {{ formatDate(config.certificate_expires_at) }}
                </span>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import routes from "../routes";
import api from "../services/api";
import permission from "../lib/permission";

export default {
  name: "NfseSettings",
  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
  },
  data() {
    return {
      loading: true,
      saving: false,
      errors: [],
      config: {
        has_focus_token_sandbox: false,
        has_focus_token_production: false,
        certificate_status: "not_sent",
        certificate_expires_at: null,
      },
      form: {
        active: false,
        environment: "sandbox",
        nfse_model: "municipal",
        city_ibge_code: "",
        company_cnpj: "",
        company_legal_name: "",
        address_street: "",
        address_number: "",
        address_complement: "",
        address_district: "",
        address_zipcode: "",
        address_state: "",
        municipal_registration: "",
        tax_regime: null,
        simples_nacional_optant: false,
        default_service_code: "",
        default_service_description: "",
        focus_token_sandbox: "",
        focus_token_production: "",
        homologation_confirmed: false,
      },
      environmentOptions: [
        { text: "Sandbox (testes)", value: "sandbox" },
        { text: "Produção", value: "production" },
      ],
      nfseModelOptions: [
        { text: "Municipal (clássico)", value: "municipal" },
        { text: "Nacional", value: "nacional" },
      ],
      taxRegimeOptions: [
        { text: "Simples Nacional", value: "simples_nacional" },
        { text: "Normal", value: "normal" },
        { text: "MEI", value: "mei" },
      ],
    };
  },
  computed: {
    canSave() {
      if (this.form.active && this.form.environment === "production") {
        return this.form.homologation_confirmed;
      }
      return true;
    },
    certificateStatusLabel() {
      const labels = {
        not_sent: "Não enviado",
        active: "Ativo",
        expiring: "Expirando em breve",
        expired: "Expirado",
      };
      return (
        labels[this.config.certificate_status] || this.config.certificate_status
      );
    },
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,

    handleToBack: () => routes.go(-1),

    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      return date.toLocaleDateString("pt-BR");
    },

    async retrieve() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/settings/nfse");
        const { error, errors, config } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.config = config;

          // preenche o form com o que veio do servidor, sem trazer os tokens
          // (eles nunca voltam em texto puro — o campo fica vazio até o usuário digitar um novo)
          this.form = {
            ...this.form,
            active: config.active,
            environment: config.environment,
            nfse_model: config.nfse_model,
            city_ibge_code: config.city_ibge_code || "",
            company_cnpj: config.company_cnpj || "",
            company_legal_name: config.company_legal_name || "",
            address_street: config.address_street || "",
            address_number: config.address_number || "",
            address_complement: config.address_complement || "",
            address_district: config.address_district || "",
            address_zipcode: config.address_zipcode || "",
            address_state: config.address_state || "",
            municipal_registration: config.municipal_registration || "",
            tax_regime: config.tax_regime,
            simples_nacional_optant: config.simples_nacional_optant,
            default_service_code: config.default_service_code || "",
            default_service_description:
              config.default_service_description || "",
            homologation_confirmed: config.homologation_confirmed,
          };
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },

    async save() {
      try {
        this.saving = true;

        const payload = { ...this.form };

        // nunca envia campo de token vazio como string vazia — deixa undefined
        // para o backend saber que é para manter o token atual
        if (!payload.focus_token_sandbox) delete payload.focus_token_sandbox;
        if (!payload.focus_token_production)
          delete payload.focus_token_production;

        const response = await api.patch("/rest-home/settings/nfse", payload);
        const { error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          // limpa os campos de token da tela após salvar, já que não voltam do servidor
          this.form.focus_token_sandbox = "";
          this.form.focus_token_production = "";
          await this.retrieve();
        }

        this.saving = false;
      } catch (error) {
        console.log(error);
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.nfse-settings-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.nfse-settings-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px 40px;
}

/* Header — mesmo padrão visual da tela de Leitos */
.nfse-settings-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

.btn-add-modern:disabled {
  opacity: 0.5 !important;
}

/* Content */
.nfse-settings-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.loading-overlay {
  min-height: 200px;
}

.settings-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid #eef1f4;
  background: #fafbfc;
}

.card-icon {
  font-size: 20px;
  color: #1b4965;
  margin-top: 3px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.card-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 2px 0 0 0;
}

.card-body {
  padding: 20px 24px 24px;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 18px;
}

.field-row:last-child {
  margin-bottom: 0;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-group-wide {
  grid-column: span 1;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.switch-group {
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.switch-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.checkbox-group {
  padding-top: 4px;
}

.alert-box {
  margin-top: 4px;
  padding: 12px 16px;
  background: #fdf1e7;
  border: 1px solid #c9702b;
  border-radius: 10px;
  color: #9a5420;
  font-size: 13px;
  font-weight: 500;
}

.cert-status-row {
  margin-top: 12px;
  font-size: 13px;
  color: #374151;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.occupied {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-badge.available {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }
  .page-title {
    justify-content: center;
  }
  .field-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .nfse-settings-container {
    padding: 0 16px 32px;
  }
  .nfse-settings-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }
  .header-content {
    padding: 0 16px;
  }
  .page-title {
    font-size: 24px;
  }
}

/* Override Vuetify */
.modern-input >>> .v-input__control {
  border-radius: 8px !important;
}

.modern-input >>> .v-text-field__slot input {
  font-weight: 500 !important;
  padding: 12px 0 !important;
}
</style>